import { useEffect, useState } from 'react';
import { Loader2, Trash2, GripVertical } from 'lucide-react';
import { FURNITURE_STYLES, FURNITURE_ROOM_TYPES, FURNITURE_ROOM_LABELS, moveItem } from '@/lib/furnitureCatalog';
import {
  listFurniturePieces,
  type FurniturePieceWithUrl,
} from '@/services/furnitureCatalogService';
import {
  listMoodboardHeaders,
  createMoodboard,
  renameMoodboard,
  deleteMoodboard,
  setMoodboardActive,
  listMoodboard,
  addToMoodboard,
  removeFromMoodboard,
  reorderMoodboard,
  listRequiredCategories,
  listBoardItemCategories,
} from '@/services/moodboardService';
import { missingCategories } from '@/lib/moodboardChecklist';
import { filterMoodboards } from '@/lib/catalogFilter';
import type { MoodboardEntry, MoodboardHeader, RequiredCategoryRow } from '@/types';

const SELECT_CLASS =
  'h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring';

export function AdminMoodboardView() {
  const [styleId, setStyleId] = useState<string>(FURNITURE_STYLES[0]);
  const [roomType, setRoomType] = useState<string>(FURNITURE_ROOM_TYPES[0]);
  const [library, setLibrary] = useState<FurniturePieceWithUrl[]>([]);
  const [board, setBoard] = useState<MoodboardEntry[]>([]);
  const [boards, setBoards] = useState<MoodboardHeader[]>([]);
  const [boardCategories, setBoardCategories] = useState<Record<string, string[]>>({});
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [required, setRequired] = useState<RequiredCategoryRow[]>([]);
  const [mbQuery, setMbQuery] = useState('');
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bibliothek laden (unabhängig von Style/Raum, nur aktive Stücke).
  useEffect(() => {
    listFurniturePieces()
      .then((lib) => setLibrary(lib.filter((p) => p.is_active)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Fehler beim Laden'));
  }, []);

  // Header-Liste + Pflicht-Kategorien bei (styleId, roomType) laden.
  useEffect(() => {
    setIsLoading(true);
    setDeleteArmed(false);
    (async () => {
      const [hdrs, req] = await Promise.all([
        listMoodboardHeaders(styleId, roomType),
        listRequiredCategories(),
      ]);
      setBoards(hdrs);
      setBoardCategories(await listBoardItemCategories(hdrs.map((h) => h.id)));
      setRequired(req);
      setSelectedBoardId(hdrs[0]?.id ?? null);
      if (hdrs.length === 0) {
        setBoard([]);
        setIsLoading(false);
      }
    })()
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
        setIsLoading(false);
      });
  }, [styleId, roomType]);

  // Item-Board bei selectedBoardId laden.
  useEffect(() => {
    setDeleteArmed(false); // Board-Wechsel entschärft einen vorher „scharfen" Löschen-Button.
    if (!selectedBoardId) {
      setBoard([]);
      return;
    }
    setIsLoading(true);
    listMoodboard(selectedBoardId)
      .then(setBoard)
      .catch((e) => setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen'))
      .finally(() => setIsLoading(false));
  }, [selectedBoardId]);

  const reloadBoard = async () => {
    if (!selectedBoardId) return;
    try {
      setBoard(await listMoodboard(selectedBoardId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
  };

  const reloadHeaders = async () => {
    try {
      const hdrs = await listMoodboardHeaders(styleId, roomType);
      setBoards(hdrs);
      setBoardCategories(await listBoardItemCategories(hdrs.map((h) => h.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
  };

  const refreshBoardCategories = async () => {
    try {
      setBoardCategories(await listBoardItemCategories(boards.map((b) => b.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
  };

  // Zwei-Schritt-Löschen zurücksetzen, sobald ein anderes Moodboard gewählt wird.
  useEffect(() => {
    setDeleteArmed(false);
  }, [selectedBoardId]);

  const requiredForRoom = required.filter((r) => r.room_type === roomType).map((r) => r.category);
  // Vollständigkeit pro Board über eine einzige Sammel-Query (boardCategories) statt
  // N Ladevorgänge — gilt ehrlich für ALLE Boards, nicht nur das gerade geöffnete.
  const isBoardComplete = (id: string) =>
    missingCategories(boardCategories[id] ?? [], requiredForRoom).length === 0;
  const openBoardMissing = selectedBoardId
    ? missingCategories(boardCategories[selectedBoardId] ?? [], requiredForRoom)
    : [];
  const visibleBoards = filterMoodboards(boards, { query: mbQuery, incompleteOnly }, isBoardComplete);
  const selectedBoard = boards.find((b) => b.id === selectedBoardId) ?? null;

  const inBoard = new Set(board.map((b) => b.pieceId));
  const palette = library.filter((p) => !inBoard.has(p.id));

  // Fügt ein Bibliotheks-Stück ans Board-Ende an. Von Klick, Panel-Drop und Karten-Drop genutzt.
  const addPiece = async (pieceId: string) => {
    if (!selectedBoardId) return;
    try {
      await addToMoodboard(selectedBoardId, pieceId);
      await reloadBoard();
      await refreshBoardCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hinzufügen fehlgeschlagen');
    }
  };

  // Palette-Karte → Board-Drop (freie Fläche): fügt Stück hinzu
  const handleBoardDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let payload: { kind: string; pieceId?: string; fromIndex?: number };
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (payload.kind !== 'palette' || !payload.pieceId) return;
    await addPiece(payload.pieceId);
  };

  // Drop auf eine bestehende Board-Karte: Palette-Stück → hinzufügen, Board-Stück → umsortieren
  const handleReorderDrop = async (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedBoardId) return;
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let payload: { kind: string; pieceId?: string; fromIndex?: number };
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    // Palette-Stück auf eine Karte fallen gelassen → hinzufügen, statt den Drop still zu schlucken
    if (payload.kind === 'palette' && payload.pieceId) {
      await addPiece(payload.pieceId);
      return;
    }
    if (payload.kind !== 'board' || payload.fromIndex === undefined) return;
    const reordered = moveItem(board, payload.fromIndex, toIndex);
    setBoard(reordered);
    try {
      await reorderMoodboard(reordered.map((b) => b.itemId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sortieren fehlgeschlagen');
      await reloadBoard();
    }
  };

  const handleRemove = async (itemId: string) => {
    setBoard((prev) => prev.filter((b) => b.itemId !== itemId));
    try {
      await removeFromMoodboard(itemId);
      await refreshBoardCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Entfernen fehlgeschlagen');
      await reloadBoard();
    }
  };

  const handleCreateBoard = async () => {
    try {
      const id = await createMoodboard(styleId, roomType, `Moodboard ${boards.length + 1}`);
      await reloadHeaders();
      setSelectedBoardId(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erstellen fehlgeschlagen');
    }
  };

  const handleRenameBoard = async () => {
    if (!selectedBoard) return;
    const name = prompt('Neuer Name für das Moodboard:', selectedBoard.name);
    if (!name || name.trim() === '' || name === selectedBoard.name) return;
    try {
      await renameMoodboard(selectedBoard.id, name.trim());
      await reloadHeaders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Umbenennen fehlgeschlagen');
    }
  };

  const handleToggleActive = async () => {
    if (!selectedBoard) return;
    try {
      await setMoodboardActive(selectedBoard.id, !selectedBoard.isActive);
      await reloadHeaders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktualisieren fehlgeschlagen');
    }
  };

  const handleDeleteBoard = async () => {
    if (!selectedBoard) return;
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }
    setDeleteArmed(false);
    try {
      await deleteMoodboard(selectedBoard.id);
      await reloadHeaders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Moodboards</h2>
        <p className="text-sm text-muted-foreground">
          Ziehe Möbel aus der Bibliothek ins Moodboard-Grid für die gewählte Kombination.
        </p>
      </div>

      <div className="flex gap-3">
        <select className={SELECT_CLASS} value={styleId} onChange={(e) => setStyleId(e.target.value)}>
          {FURNITURE_STYLES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select className={SELECT_CLASS} value={roomType} onChange={(e) => setRoomType(e.target.value)}>
          {FURNITURE_ROOM_TYPES.map((r) => (
            <option key={r} value={r}>
              {FURNITURE_ROOM_LABELS[r] ?? r}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          value={mbQuery}
          onChange={(e) => setMbQuery(e.target.value)}
          placeholder="Moodboard suchen…"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        />
        <label className="flex items-center gap-1 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={incompleteOnly}
            onChange={(e) => setIncompleteOnly(e.target.checked)}
          />
          Nur unvollständige
        </label>
        <button
          type="button"
          onClick={handleCreateBoard}
          className="h-9 rounded-md bg-primary px-3 text-sm text-primary-foreground"
        >
          + Neues Moodboard
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-2">
        {visibleBoards.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">Keine Moodboards für diese Auswahl.</p>
        ) : (
          visibleBoards.map((b) => {
            const complete = isBoardComplete(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => setSelectedBoardId(b.id)}
                className={`rounded-lg border px-3 py-2 text-sm ${b.id === selectedBoardId ? 'border-primary bg-primary/10' : 'border-muted'}`}
              >
                <span className="font-medium">{b.name}</span>
                {!b.isActive && <span className="ml-2 text-xs text-muted-foreground">(inaktiv)</span>}
                {b.id === selectedBoardId && (
                  <span className={`ml-2 text-xs ${complete ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {complete ? '✓ vollständig' : `fehlt: ${openBoardMissing.join(', ')}`}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>

      {selectedBoard && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            type="button"
            onClick={handleRenameBoard}
            className="h-8 rounded-md border border-input px-2 text-xs"
          >
            Umbenennen
          </button>
          <button
            type="button"
            onClick={handleToggleActive}
            className="h-8 rounded-md border border-input px-2 text-xs"
          >
            {selectedBoard.isActive ? 'Deaktivieren' : 'Aktivieren'}
          </button>
          <button
            type="button"
            onClick={handleDeleteBoard}
            onBlur={() => setDeleteArmed(false)}
            className={`h-8 rounded-md border px-2 text-xs ${deleteArmed ? 'border-destructive bg-destructive/10 text-destructive' : 'border-input'}`}
          >
            {deleteArmed ? 'Wirklich löschen?' : 'Löschen'}
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {/* Bibliothek (Palette) */}
          <div>
            <p className="text-sm font-medium mb-2">Bibliothek</p>
            <div className="grid grid-cols-3 gap-2 min-h-[120px] max-h-[60vh] overflow-y-auto p-2 rounded-lg border border-border bg-muted/20">
              {palette.length === 0 ? (
                <p className="col-span-3 text-xs text-muted-foreground text-center py-6">
                  Keine weiteren aktiven Stücke.
                </p>
              ) : (
                palette.map((piece) => (
                  <div
                    key={piece.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        'text/plain',
                        JSON.stringify({ kind: 'palette', pieceId: piece.id }),
                      );
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => addPiece(piece.id)}
                    title="Klicken zum Hinzufügen"
                    className="rounded border border-border bg-background overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/50 select-none"
                  >
                    <img
                      src={piece.url}
                      alt={piece.category}
                      draggable={false}
                      className="w-full aspect-square object-contain"
                    />
                    <p className="text-[10px] truncate px-1 py-0.5">{piece.name || piece.category}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Moodboard-Grid (Drop-Target) */}
          <div className="sticky top-4 self-start">
            <p className="text-sm font-medium mb-2">
              {selectedBoard ? selectedBoard.name : `Moodboard ${styleId} / ${roomType}`}
            </p>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDrop={handleBoardDrop}
              className="grid grid-cols-3 gap-2 min-h-[120px] p-2 rounded-lg border-2 border-dashed border-border bg-primary/5"
            >
              {!selectedBoardId ? (
                <p className="col-span-3 text-xs text-muted-foreground text-center py-6">
                  Bitte zuerst ein Moodboard anlegen oder auswählen.
                </p>
              ) : board.length === 0 ? (
                <p className="col-span-3 text-xs text-muted-foreground text-center py-6">
                  Stücke hierher ziehen.
                </p>
              ) : (
                board.map((entry, index) => (
                  <div
                    key={entry.itemId}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        'text/plain',
                        JSON.stringify({ kind: 'board', fromIndex: index }),
                      );
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleReorderDrop(e, index)}
                    className="relative rounded border border-border bg-background overflow-hidden cursor-grab active:cursor-grabbing select-none"
                  >
                    <img
                      src={entry.url}
                      alt={entry.category}
                      draggable={false}
                      className="w-full aspect-square object-contain"
                    />
                    <div className="flex items-center justify-between px-1 py-0.5">
                      <GripVertical className="w-3 h-3 text-muted-foreground" />
                      <span className="text-[10px] truncate flex-1 px-1">
                        {entry.name || entry.category}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemove(entry.itemId)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
